import { jsPDF } from 'jspdf';

export const generateScanAnalysisPDF = async (
  allDiagnoses: any[],
  previews: string[],
  selectedFiles: File[],
  userInfo?: { fullName: string; email: string },
  returnBlob: boolean = false
): Promise<Blob | void> => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  const margin = 20;
  
  // Add OrthoMind logo
  const logoImg = new Image();
  logoImg.src = '/logo_small.png';
  await new Promise((resolve) => {
    logoImg.onload = () => resolve(null);
  });
  
  // FIRST PAGE - Analysis Results
  // Add logo at top
  pdf.addImage(logoImg, 'PNG', margin, 15, 20, 20);
  
  // Add title next to logo
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('OrthoMind - Smart Vision Analysis', 45, 27);
  
  // Add date
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(new Date().toLocaleDateString(), 45, 34);
  
  // Add border line
  pdf.setDrawColor(220, 220, 220);
  pdf.line(margin, 45, pageWidth - margin, 45);
  
  let yPosition = 55;
  
  // Add patient information section if available
  if (userInfo?.fullName || userInfo?.email) {
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Information', margin, yPosition);
    yPosition += 10;
    
    if (userInfo?.fullName) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Name: ', margin, yPosition);
      pdf.setFont('helvetica', 'bold');
      pdf.text(userInfo.fullName, margin + 15, yPosition);
      yPosition += 8;
    }
    
    if (userInfo?.email) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Email: ', margin, yPosition);
      pdf.setFont('helvetica', 'bold');
      pdf.text(userInfo.email, margin + 15, yPosition);
      yPosition += 8;
    }
    
    // Add separator line
    yPosition += 5;
    pdf.setDrawColor(240, 240, 240);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;
  }
  
  // OrthoVision Analysis section
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('OrthoVision Analysis', margin, yPosition);
  yPosition += 12;
  
  // Show all unique diagnoses
  if (allDiagnoses.length > 0) {
    allDiagnoses.forEach((diagnosis: any) => {
      // Check if we need more space
      if (yPosition > pageHeight - 80) {
        // Add disclaimer before new page
        addDisclaimer(pdf, pageHeight, margin, pageWidth);
        pdf.addPage();
        yPosition = margin;
      }
      
      // Diagnosis box
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(margin, yPosition - 5, pageWidth - 2 * margin, 30, 3, 3, 'F');
      
      // Field name
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatFieldName(diagnosis.field_name), margin + 5, yPosition + 2);
      
      // Field value
      yPosition += 8;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const valueColor = diagnosis.field_value.toLowerCase() === 'yes' ? [0, 128, 0] : 
                        diagnosis.field_value.toLowerCase() === 'no' ? [128, 128, 128] : 
                        [0, 0, 0];
      pdf.setTextColor(valueColor[0], valueColor[1], valueColor[2]);
      pdf.text(formatFieldValue(diagnosis.field_value), margin + 5, yPosition + 2);
      pdf.setTextColor(0, 0, 0);
      
      // Explanation
      yPosition += 8;
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      const explanation = getFieldExplanation(diagnosis.field_name, diagnosis.field_value);
      const explanationLines = pdf.splitTextToSize(explanation, pageWidth - 2 * margin - 10);
      explanationLines.forEach((line: string) => {
        pdf.text(line, margin + 5, yPosition);
        yPosition += 5;
      });
      pdf.setTextColor(0, 0, 0);
      
      yPosition += 10;
    });
  } else {
    pdf.setFontSize(11);
    pdf.text('No clinical findings detected in the analyzed images.', margin, yPosition);
  }
  
  // Add disclaimer at bottom of first page
  addDisclaimer(pdf, pageHeight, margin, pageWidth);
  
  // ATTACHMENT PAGES - Images
  if (previews.length > 0) {
    pdf.addPage();
    
    // Attachment header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Attachments - Analyzed Images', margin, 30);
    
    pdf.setDrawColor(220, 220, 220);
    pdf.line(margin, 35, pageWidth - margin, 35);
    
    let imageY = 45;
    let imagesOnPage = 0;
    
    for (let i = 0; i < previews.length; i++) {
      if (imagesOnPage === 2) {
        // Max 2 images per page
        pdf.addPage();
        imageY = 30;
        imagesOnPage = 0;
      }
      
      const img = new Image();
      img.src = previews[i];
      
      // Capture current values in closure
      const currentImageY = imageY;
      const currentImageIndex = i;
      
      await new Promise((resolve) => {
        img.onload = () => {
          // Calculate dimensions
          const maxWidth = pageWidth - 2 * margin;
          const maxHeight = 120;
          let imgWidth = img.width;
          let imgHeight = img.height;
          const aspectRatio = imgWidth / imgHeight;
          
          if (imgWidth > maxWidth) {
            imgWidth = maxWidth;
            imgHeight = imgWidth / aspectRatio;
          }
          
          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * aspectRatio;
          }
          
          // Center the image
          const imgX = margin + (maxWidth - imgWidth) / 2;
          
          // Add image with border
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(imgX - 1, currentImageY - 1, imgWidth + 2, imgHeight + 2);
          pdf.addImage(img, 'JPEG', imgX, currentImageY, imgWidth, imgHeight);
          
          // Add image number
          pdf.setFontSize(10);
          pdf.setTextColor(100, 100, 100);
          pdf.text(`Image ${currentImageIndex + 1} of ${previews.length}`, pageWidth / 2, currentImageY + imgHeight + 8, { align: 'center' });
          pdf.setTextColor(0, 0, 0);
          
          imageY += imgHeight + 20;
          imagesOnPage++;
          
          resolve(null);
        };
      });
    }
  }
  
  // Save the PDF with patient name if available
  const dateStr = new Date().toISOString().split('T')[0];
  const patientName = userInfo?.fullName ? `-${userInfo.fullName.replace(/\s+/g, '-')}` : '';
  const filename = `OrthoMind-smart-scan-analysis${patientName}-${dateStr}.pdf`;
  
  if (returnBlob) {
    // Return blob for server upload
    return pdf.output('blob');
  } else {
    // Download to user's device
    pdf.save(filename);
  }
};

function addDisclaimer(pdf: jsPDF, pageHeight: number, margin: number, pageWidth: number) {
  // Disclaimer box
  const disclaimerY = pageHeight - 40;
  pdf.setFillColor(255, 251, 235);
  pdf.roundedRect(margin, disclaimerY, pageWidth - 2 * margin, 25, 3, 3, 'F');
  
  // Disclaimer text
  pdf.setFontSize(9);
  pdf.setTextColor(100, 100, 100);
  pdf.setFont('helvetica', 'italic');
  const disclaimer = 'This AI-generated analysis is intended to assist orthodontists in their clinical assessment. It should be used in conjunction with professional clinical examination and judgment. Always verify findings with direct patient examination.';
  const disclaimerLines = pdf.splitTextToSize(disclaimer, pageWidth - 2 * margin - 10);
  let dY = disclaimerY + 5;
  disclaimerLines.forEach((line: string) => {
    pdf.text(line, margin + 5, dY);
    dY += 4;
  });
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
}

function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

function formatFieldValue(value: string): string {
  if (value.toLowerCase() === 'yes') return '✓ Present';
  if (value.toLowerCase() === 'no') return '✗ Not Present';
  return value;
}

function getFieldExplanation(fieldName: string, fieldValue: string): string {
  const explanations: { [key: string]: { [value: string]: string } } = {
    'Malocclusion type': {
      'Class I': 'Normal bite relationship with properly aligned molars. May still have crowding or spacing issues.',
      'Class II': 'Upper teeth protrude forward relative to lower teeth (overjet). Often called "overbite" by patients.',
      'Class III': 'Lower teeth protrude forward relative to upper teeth (underbite). May require surgical intervention in severe cases.'
    },
    'All permanent teeth present': {
      'Yes': 'Complete adult dentition is present, which provides more treatment options.',
      'No': 'Missing teeth may require special consideration for space management and treatment planning.'
    },
    'Crowding on upper anterior region': {
      'Yes': 'Insufficient space for teeth alignment in the upper front area. May require expansion or extractions.',
      'No': 'Adequate space available in the upper front teeth area.'
    },
    'Crowding on lower anterior region': {
      'Yes': 'Insufficient space for teeth alignment in the lower front area. Common in adult patients.',
      'No': 'Adequate space available in the lower front teeth area.'
    },
    'Maxillary transverse constriction in premolar region': {
      'Yes': 'Narrow upper jaw in the premolar area. May benefit from palatal expansion.',
      'No': 'Normal width relationship between upper and lower jaws.'
    },
    'Spee curve on lower arch': {
      'Yes': 'Pronounced curve in the lower arch. Leveling will require vertical space.',
      'No': 'Relatively flat occlusal plane in the lower arch.'
    },
    'Upper midline aligned with face midline': {
      'Yes': 'Dental midline matches facial midline, which is aesthetically ideal.',
      'No': 'Dental midline deviation present. May require asymmetric mechanics to correct.'
    },
    'Distalize more than 1mm': {
      'Yes': 'Significant backward movement of teeth required. May need TADs or special mechanics.',
      'No': 'Minimal or no distalization needed, simplifying treatment.'
    }
  };

  const fieldKey = Object.keys(explanations).find(key => 
    fieldName.toLowerCase().includes(key.toLowerCase())
  );

  if (fieldKey && explanations[fieldKey][fieldValue]) {
    return explanations[fieldKey][fieldValue];
  }

  if (fieldValue.toLowerCase() === 'yes') {
    return 'This finding is present and will be considered in the treatment plan.';
  } else if (fieldValue.toLowerCase() === 'no') {
    return 'This finding is not present, which may simplify treatment.';
  }

  return 'This finding will be evaluated as part of the comprehensive treatment plan.';
}